import type { EngineDocument, SceneNode } from "../scene/model";
import { activePage } from "../native/pages";
import { hasPaint, strokeRadius } from "../native/appearance";
import { isNativeShape } from "../native/shapes";
import { vectorPath } from "../native/vector-path";

/** Private render nodes, never written into the editor document or its history.
 * Plain metadata survives structured cloning into the raster worker. */
type PaintNode = SceneNode & {
  clippingPaint?: { source: string; part: "fill" | "stroke" | "shadow" };
};
export const clippingPaintSource = (node: SceneNode) =>
  (node as PaintNode).clippingPaint?.source ?? node.id;
export const clippingShadowOnly = (node: SceneNode) =>
  (node as PaintNode).clippingPaint?.part === "shadow";
export const clippingBorderSource = (node: SceneNode) =>
  (node as PaintNode).clippingPaint?.part === "stroke"
    ? clippingPaintSource(node)
    : undefined;
export function hasClippingBorder(node: SceneNode) {
  return (
    !(node as PaintNode).clippingPaint &&
    strokeRadius(node) > 0 &&
    (isNativeShape(node) ||
      (node.kind === "path" &&
        (!!node.geometry.svgPath || vectorPath(node).closed)))
  );
}
const fills = new WeakMap<SceneNode, SceneNode>();
/** A shape's outline and shadow are decoration, not image-mask coverage. */
export function clippingFill(node: SceneNode): SceneNode {
  if (!hasClippingBorder(node)) return node;
  let fill = fills.get(node);
  if (!fill) {
    fill = {
      ...node,
      appearance: { ...node.appearance, strokeEnabled: false },
      effects: { ...node.effects, shadow: false },
    };
    fills.set(node, fill);
  }
  return fill;
}
const documents = new WeakMap<EngineDocument, EngineDocument>();
const parts = new WeakMap<SceneNode, PaintNode[]>();
/** Paint each contiguous clipping stack as shadow, fill, images, outline.
 * The outline occurs once, after ALL clipped images, before the next layer. */
export function clippingPaintDocument(
  document: EngineDocument,
  onlyIds?: ReadonlySet<string>,
): EngineDocument {
  if (!onlyIds && documents.has(document)) return documents.get(document)!;
  const page = activePage(document),
    nodes: SceneNode[] = [];
  const existing = new Set(page.nodes.map((n) => n.id));
  let changed = false;
  for (let i = 0; i < page.nodes.length; i++) {
    const base = page.nodes[i]!;
    let end = i + 1;
    while (page.nodes[end]?.layer.clipToBelow) end++;
    const split =
      hasClippingBorder(base) &&
      !base.layer.clipToBelow &&
      base.appearance.visible !== false &&
      (!onlyIds || onlyIds.has(base.id)) &&
      page.nodes
        .slice(i + 1, end)
        .some(
          (n) =>
            n.appearance.visible !== false &&
            hasPaint(n) &&
            (!onlyIds || onlyIds.has(n.id)),
        );
    if (!split) {
      nodes.push(base);
      continue;
    }
    let layers = parts.get(base);
    if (!layers || layers.some((n) => n.id !== base.id && existing.has(n.id))) {
      const part = (
        kind: "fill" | "stroke" | "shadow",
        node: SceneNode,
      ): PaintNode => {
        let id = kind === "fill" ? base.id : `clipping-${kind}:${base.id}`;
        while (kind !== "fill" && existing.has(id)) id += ":";
        return { ...node, id, clippingPaint: { source: base.id, part: kind } };
      };
      layers = [
        part("fill", clippingFill(base)),
        part("stroke", {
          ...base,
          appearance: { ...base.appearance, fillEnabled: false },
          effects: { ...base.effects, shadow: false },
        }),
        ...(base.effects.shadow ? [part("shadow", base)] : []),
      ];
      parts.set(base, layers);
    }
    if (layers[2]) nodes.push(layers[2]);
    nodes.push(layers[0]!, ...page.nodes.slice(i + 1, end), layers[1]!);
    i = end - 1;
    changed = true;
  }
  const result = changed
    ? {
        ...document,
        pages: document.pages.map((p) => (p === page ? { ...p, nodes } : p)),
      }
    : document;
  if (!onlyIds) {
    documents.set(document, result);
    documents.set(result, result);
  }
  return result;
}
